import { useAuth } from '../../contexts/AuthContext';
import { Navigate } from 'react-router-dom';

/**
 * Permission gate — hides children if user lacks required permission.
 * Usage: <PermissionGate module="leads" action="view"><LeadsPage /></PermissionGate>
 * For routes: <PermissionGate module="leads" action="view" redirect><LeadsPage /></PermissionGate>
 */
export function PermissionGate({ module, action, children, redirect = false, fallback = null }) {
  const { hasPermission, user, permissions } = useAuth();

  if (!user) return redirect ? <Navigate to="/login" replace /> : null;
  if (user.is_super_admin) return children;
  
  // action="*" means any permission in the module grants access
  if (action === '*') {
    const modulePerms = permissions[module] || [];
    const generalPerms = module === 'settings' ? (permissions['general'] || []) : [];
    if (modulePerms.length > 0 || generalPerms.length > 0) return children;
  } else if (hasPermission(module, action)) {
    return children;
  }

  if (redirect) {
    // Redirect to first accessible route instead of hardcoded /dashboard
    const target = findFirstAccessible(permissions, user);
    return <Navigate to={target} replace />;
  }
  return fallback || null;
}

// Find first accessible route based on user permissions
function findFirstAccessible(perms, user) {
  if (user?.is_super_admin) return '/dashboard';
  const NAV_PERMS = [
    { path: '/dashboard', module: 'stats' },
    { path: '/leads', module: 'leads' },
    { path: '/front-desk-tasks', module: 'front_desk_tasks' },
    { path: '/call-tracker', module: 'call_tracker' },
    { path: '/inbox', module: 'inbox' },
    { path: '/scheduling', module: 'scheduling' },
  ];
  let ordered = [...NAV_PERMS];
  try {
    const navOrder = JSON.parse(localStorage.getItem('nav_order') || '[]');
    if (navOrder.length > 0) {
      ordered = navOrder.map(p => NAV_PERMS.find(n => n.path === p)).filter(Boolean);
      for (const item of NAV_PERMS) {
        if (!ordered.some(o => o.path === item.path)) ordered.push(item);
      }
    }
  } catch {}
  for (const item of ordered) {
    if ((perms[item.module] || []).length > 0) return item.path;
  }
  return '/dashboard';
}

/**
 * Hook for checking permissions inline.
 * Usage: const can = usePermission(); if (can('leads', 'edit')) { ... }
 */
export function usePermission() {
  const { hasPermission, user } = useAuth();
  return (module, action) => {
    if (!user) return false;
    if (user.is_super_admin) return true;
    return hasPermission(module, action);
  };
}

/**
 * Check if user has ANY permission in a module (for nav visibility).
 */
export function useModuleAccess() {
  const { permissions, user } = useAuth();
  return (module) => {
    if (!user) return false;
    if (user.is_super_admin) return true;
    const modulePerms = permissions[module] || [];
    if (modulePerms.length > 0) return true;
    // Backward compat: "general" merged into "settings"
    if (module === 'settings') {
      const generalPerms = permissions['general'] || [];
      return generalPerms.length > 0;
    }
    return false;
  };
}
